/**
 * App-wide offline gate for a page. When the device is offline AND the active
 * page has no cached data, it shows the "no internet connection" screen instead
 * of the page (which would otherwise spin or show a misleading "no data" state).
 *
 * If the page DOES have cached data (even an empty result that loaded successfully
 * while online), the page renders normally — its own empty state is the truthful
 * one. Pages not listed here have nothing cached for offline use, so they always
 * show the offline screen when offline.
 */
import { useSyncExternalStore } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useOnlineStatus } from '@/hooks/useOnlineStatus'
import { OfflineFallback } from './ui/OfflineFallback'

// Dashboard tab -> the persisted query-key prefix that holds its data.
const TAB_QUERY_PREFIX: Record<string, string> = {
  jobs: 'jobs',
  invoices: 'invoices',
  estimates: 'estimates',
  contacts: 'contacts',
  calendar: 'calendar',
  messaging: 'conversations',
}

export function OfflineBoundary({ tab, children }: { tab: string; children: React.ReactNode }) {
  const isOnline = useOnlineStatus()
  const queryClient = useQueryClient()
  const prefix = TAB_QUERY_PREFIX[tab]

  // Re-evaluate whenever the cache changes (e.g. data arrives after reconnect).
  const hasCachedData = useSyncExternalStore(
    (cb) => queryClient.getQueryCache().subscribe(cb),
    () =>
      !!prefix &&
      queryClient
        .getQueryCache()
        .getAll()
        .some((q) => q.queryKey[0] === prefix && q.state.status === 'success'),
  )

  if (isOnline || hasCachedData) return <>{children}</>

  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <OfflineFallback
        onRetry={() => prefix && queryClient.refetchQueries({ queryKey: [prefix] })}
      />
    </div>
  )
}
